/* EntityQuickPeek — a reusable, STACKABLE, nested pop-up "mini-360" modal.
 *
 * Drilling a related entity inside a peek PUSHES another peek onto an internal
 * stack with a breadcrumb trail (e.g. `Coconuts > Jane > Deal`). Close/back pops
 * the stack; an "Open full record" button navigates to the full record. It is
 * entity-type-agnostic (org / person / deal / project / lead) and fetches the
 * deterministic `/api/crm/360` resolver for a compact read-only mini view
 * (header + key info + relationships + rollups).
 *
 * Architecture (single visible modal + breadcrumb, not N stacked backdrops):
 *   - `useEntityPeek()` (called once in App.tsx) owns the stack state.
 *   - ONE `<EntityQuickPeekHost>` is mounted once in the shell. It renders only
 *     the TOP-of-stack peek as the active modal (lower entries are breadcrumb
 *     crumbs). This avoids stacking backdrops while preserving drill history.
 *
 * Fail-safe: a peek fetch error renders a small "couldn't load" shell, never a
 * blank backdrop. Lazy-loaded by App.tsx via React.lazy + <Suspense>.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { getEntity360 } from '../api'
import type { Entity360Type } from '../lib/entity360/registry'

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------
export type PeekEntity = { type: Entity360Type; id: string; label?: string }

/** Raw resolver row shape (resolver emits primaryLabel/secondaryLabel/meta/…,
 *  NOT the registry's title/subtitle). Kept loose + defensive. */
interface RawItem {
  id?: string | number
  primaryLabel?: string
  secondaryLabel?: string | null
  badges?: string[]
  href?: string | null
  meta?: Record<string, unknown>
  nested?: RawItem[]
  [k: string]: unknown
}
interface RawSection {
  key: string
  title?: string
  layout?: string
  items?: RawItem[]
  rollups?: Record<string, unknown>
  [k: string]: unknown
}
interface RawEntity {
  id?: string
  type?: string
  name?: string
  sub_type?: string | null
  email?: string | null
  phone?: string | null
  address?: { street?: string | null; city?: string | null; state?: string | null; zip?: string | null }
  merged_into?: string | null
}
interface RawPayload {
  entity?: RawEntity
  sections?: RawSection[]
  rollups?: Record<string, unknown>
  warnings?: unknown[]
}

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
const money = (n: unknown): string => usd.format(typeof n === 'number' && isFinite(n) ? n : 0)

// Map a peek entity type → the full-record hash route segment.
const ROUTE_FOR_TYPE: Record<Entity360Type, string> = {
  org: 'organizations',
  person: 'contacts',
  deal: 'deals',
  project: 'projects',
  lead: 'leads',
}

// Which resolver sections carry drillable related entities, and the peek type
// each row resolves to. A row with no resolvable type is shown but not clickable.
function peekTargetForSection(sectionKey: string, item: RawItem): PeekEntity | null {
  const idStr = item?.id != null ? String(item.id) : ''
  switch (sectionKey) {
    case 'people':
    case 'contacts':
    case 'contact':
      return idStr ? { type: 'person', id: idStr, label: item.primaryLabel } : null
    case 'org_rels':
    case 'orgs':
    case 'org_roles': {
      // org_rels rows carry the related org id in meta.related_org_id; org_roles
      // (person side) carry it in meta.org_id; orgs (deal/project) use item.id.
      const rid =
        (item.meta?.related_org_id as string | undefined) ||
        (item.meta?.org_id as string | undefined) ||
        idStr
      return rid ? { type: 'org', id: String(rid), label: item.primaryLabel } : null
    }
    case 'deals':
    case 'deal':
      return idStr ? { type: 'deal', id: idStr, label: item.primaryLabel } : null
    case 'projects':
      return idStr ? { type: 'project', id: idStr, label: item.primaryLabel } : null
    case 'leads':
      return idStr ? { type: 'lead', id: idStr, label: item.primaryLabel } : null
    default:
      return null
  }
}

// ---------------------------------------------------------------------------
// useEntityPeek — owns the stack. Mount the host once; call push() from rows.
// ---------------------------------------------------------------------------
const MAX_STACK = 6

export function useEntityPeek() {
  const [stack, setStack] = useState<PeekEntity[]>([])

  const push = useCallback((e: PeekEntity) => {
    if (!e || !e.type || !e.id) return
    setStack((prev) => {
      // Dedup: if {type,id} is already anywhere on the stack, truncate back to
      // it instead of pushing a duplicate (prevents A→B→A loops).
      const existingIdx = prev.findIndex((p) => p.type === e.type && String(p.id) === String(e.id))
      if (existingIdx >= 0) return prev.slice(0, existingIdx + 1)
      if (prev.length >= MAX_STACK) return prev // cap; ignore deeper drills
      return [...prev, e]
    })
  }, [])

  const pop = useCallback(() => setStack((prev) => prev.slice(0, -1)), [])
  const reset = useCallback(() => setStack([]), [])
  const truncateTo = useCallback((i: number) => setStack((prev) => prev.slice(0, i + 1)), [])

  return { stack, push, pop, reset, truncateTo }
}

export type EntityPeekController = ReturnType<typeof useEntityPeek>

// ---------------------------------------------------------------------------
// Host — renders only the TOP-of-stack peek (lower entries = breadcrumb).
// ---------------------------------------------------------------------------
export function EntityQuickPeekHost({
  controller,
  onOpenFull,
}: {
  controller: EntityPeekController
  /** Close the stack + navigate to the full record for this entity. */
  onOpenFull: (e: PeekEntity) => void
}) {
  const { stack, push, pop, reset, truncateTo } = controller
  const top = stack.length ? stack[stack.length - 1] : null

  // Esc pops one level (or closes at root). Bound only while a peek is open.
  useEffect(() => {
    if (!top) return
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') { ev.stopPropagation(); pop() }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [top, pop])

  if (!top) return null

  return createPortal(
    <div className="eqp-overlay" onClick={reset}>
      <div className="eqp-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <PeekBreadcrumb stack={stack} onCrumb={truncateTo} />
        <PeekBody
          key={`${top.type}:${top.id}`}
          entity={top}
          canBack={stack.length > 1}
          onBack={pop}
          onClose={reset}
          onDrill={push}
          onOpenFull={() => onOpenFull(top)}
        />
      </div>
    </div>,
    document.body,
  )
}

function PeekBreadcrumb({ stack, onCrumb }: { stack: PeekEntity[]; onCrumb: (i: number) => void }) {
  if (stack.length <= 1) return null
  return (
    <div className="eqp-breadcrumb">
      {stack.map((e, i) => (
        <span key={`${e.type}:${e.id}:${i}`} className="eqp-crumb-wrap">
          {i > 0 && <span className="eqp-crumb-sep">›</span>}
          <button
            type="button"
            className={`eqp-crumb${i === stack.length - 1 ? ' eqp-crumb--active' : ''}`}
            onClick={() => onCrumb(i)}
            disabled={i === stack.length - 1}
          >
            {e.label || e.id}
          </button>
        </span>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// PeekBody — fetches + renders one mini-360. Re-mounted (via key) per top entity.
// ---------------------------------------------------------------------------
function PeekBody({
  entity,
  canBack,
  onBack,
  onClose,
  onDrill,
  onOpenFull,
}: {
  entity: PeekEntity
  canBack: boolean
  onBack: () => void
  onClose: () => void
  onDrill: (e: PeekEntity) => void
  onOpenFull: () => void
}) {
  const [payload, setPayload] = useState<RawPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [errored, setErrored] = useState(false)
  const ctrlRef = useRef<AbortController | null>(null)

  useEffect(() => {
    const ctrl = new AbortController()
    ctrlRef.current = ctrl
    let alive = true
    setLoading(true)
    setErrored(false)
    getEntity360(entity.type, entity.id, { depth: 1, signal: ctrl.signal })
      .then((res) => {
        if (!alive) return
        setPayload(res as unknown as RawPayload)
        setLoading(false)
      })
      .catch((err) => {
        if (!alive || ctrl.signal.aborted) return
        setErrored(true)
        setLoading(false)
        // eslint-disable-next-line no-console
        console.warn('entity_quick_peek_fetch_failed', String((err as Error)?.message || err))
      })
    return () => { alive = false; ctrl.abort() }
  }, [entity.type, entity.id])

  const ent = payload?.entity
  const name = ent?.name || entity.label || entity.id
  const sub = ent?.sub_type || null
  const sections = Array.isArray(payload?.sections) ? payload!.sections! : []
  const rollups = payload?.rollups || null

  return (
    <>
      {/* Header */}
      <div className="eqp-header">
        <div className="eqp-header-main">
          {canBack && (
            <button type="button" className="eqp-back" onClick={onBack} aria-label="Back">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
          )}
          <div className="eqp-title-wrap">
            <h3 className="eqp-title">{name}</h3>
            <span className="eqp-type-chip">{prettyType(entity.type)}{sub ? ` · ${sub}` : ''}</span>
          </div>
        </div>
        <div className="eqp-header-actions">
          <button type="button" className="eqp-openfull" onClick={onOpenFull}>
            Open full record
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
          </button>
          <button type="button" className="eqp-close" onClick={onClose} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="eqp-body">
        {loading && <div className="eqp-loading">Loading…</div>}
        {!loading && errored && <div className="eqp-error">Couldn’t load this record’s details.</div>}
        {!loading && !errored && (
          <>
            <PeekKeyInfo entity={ent} type={entity.type} />
            {rollups && <PeekRollups rollups={rollups} type={entity.type} />}
            {sections.map((s) => (
              <PeekSection key={s.key} section={s} onDrill={onDrill} />
            ))}
            {sections.length === 0 && <div className="eqp-empty">No related records.</div>}
          </>
        )}
      </div>
    </>
  )
}

function prettyType(t: Entity360Type): string {
  switch (t) {
    case 'org': return 'Organization'
    case 'person': return 'Person'
    case 'deal': return 'Deal'
    case 'project': return 'Project'
    case 'lead': return 'Lead'
    default: return t
  }
}

function PeekKeyInfo({ entity, type }: { entity: RawEntity | undefined; type: Entity360Type }) {
  if (!entity) return null
  const rows: Array<{ label: string; value: string }> = []
  if (entity.email) rows.push({ label: 'Email', value: entity.email })
  if (entity.phone) rows.push({ label: 'Phone', value: entity.phone })
  const addr = entity.address
  const addrStr = addr ? [addr.street, addr.city, addr.state, addr.zip].filter(Boolean).join(', ') : ''
  if (addrStr) rows.push({ label: 'Address', value: addrStr })
  if (rows.length === 0) return null
  return (
    <div className="eqp-keyinfo">
      {rows.map((r) => (
        <div key={r.label} className="eqp-kv">
          <span className="eqp-kv-label">{r.label}</span>
          <span className="eqp-kv-value">{r.value}</span>
        </div>
      ))}
    </div>
  )
}

// Surface the most useful top-level rollups for a compact strip.
function PeekRollups({ rollups, type }: { rollups: Record<string, unknown>; type: Entity360Type }) {
  const tiles: Array<{ label: string; value: string }> = []
  const num = (v: unknown): number => (typeof v === 'number' && isFinite(v) ? v : 0)
  const od = rollups.open_deals as { count?: number; total?: number } | undefined
  const ap = rollups.active_projects as { count?: number; total?: number } | undefined
  const lw = rollups.lifetime_won as { total?: number } | undefined
  const ot = rollups.open_tickets as { count?: number } | undefined
  if (od && num(od.count) > 0) tiles.push({ label: 'Open deals', value: `${od.count} · ${money(od.total)}` })
  if (ap && num(ap.count) > 0) tiles.push({ label: 'Active projects', value: `${ap.count} · ${money(ap.total)}` })
  if (lw && num(lw.total) > 0) tiles.push({ label: 'Lifetime won', value: money(lw.total) })
  if (ot && num(ot.count) > 0) tiles.push({ label: 'Open tickets', value: String(ot.count) })
  if (tiles.length === 0) return null
  return (
    <div className="eqp-rollups">
      {tiles.map((t) => (
        <div key={t.label} className="eqp-rollup-tile">
          <div className="eqp-rollup-label">{t.label}</div>
          <div className="eqp-rollup-value">{t.value}</div>
        </div>
      ))}
    </div>
  )
}

function PeekSection({ section, onDrill }: { section: RawSection; onDrill: (e: PeekEntity) => void }) {
  const items = Array.isArray(section.items) ? section.items : []
  if (items.length === 0) return null
  return (
    <div className="eqp-section">
      <div className="eqp-section-title">{section.title || section.key}</div>
      <div className="eqp-section-body">
        {items.slice(0, 12).map((it, i) => {
          const target = peekTargetForSection(section.key, it)
          const amount =
            (it.meta?.opportunity as number | undefined) ??
            (it.meta?.amount as number | undefined) ??
            (it.meta?.contract_value as number | undefined)
          return (
            <div key={(it.id != null ? String(it.id) : '') + i} className="eqp-row">
              <div className="eqp-row-main">
                {target ? (
                  <button type="button" className="eqp-row-name eqp-row-name--link" onClick={() => onDrill(target)}>
                    {it.primaryLabel || '(untitled)'}
                  </button>
                ) : (
                  <span className="eqp-row-name">{it.primaryLabel || '(untitled)'}</span>
                )}
                {Array.isArray(it.badges) && it.badges.map((b) => (
                  <span key={b} className="eqp-badge">{b}</span>
                ))}
                {it.secondaryLabel && <span className="eqp-row-sub">{it.secondaryLabel}</span>}
              </div>
              {typeof amount === 'number' && amount > 0 && <span className="eqp-row-amount">{money(amount)}</span>}
            </div>
          )
        })}
        {items.length > 12 && <div className="eqp-row-more">+{items.length - 12} more</div>}
      </div>
    </div>
  )
}
